import React from 'react';
import { motion } from 'framer-motion';
import { BadgeCheckIcon, BookOpenCheckIcon, CheckIcon, CircleDollarSignIcon, HeartHandshakeIcon, ShieldCheckIcon, SparklesIcon, StarIcon } from 'lucide-react';
import { Countdown } from '../components/Countdown';
import { CHECKOUT_URL, CtaButton, SecureNote } from '../components/CtaButton';
import { PurchaseNotification } from '../components/PurchaseNotification';
import { useScreenInit } from '../useScreenInit';
const images = {
  product: 'https://cdn.magicpatterns.com/uploads/8VnTskHcdzMTkNk3qNUXAy/MOCKUP_1_CODIGO.png',
  bonus1: 'https://cdn.magicpatterns.com/uploads/vXMq5HLkg3UwZSxHtetUbs/BONO_1_CODIGO.png',
  bonus2: 'https://cdn.magicpatterns.com/uploads/5f43t3q1iUKZkKchtoaf1N/BONO_2_CODIGO.png',
  bonus3: 'https://cdn.magicpatterns.com/uploads/xnEzAsundY65agYYTuX67J/BONO_3_CODIGO.png',
  bonus4: 'https://cdn.magicpatterns.com/uploads/vrWiBRSDENJbPPdm3VZ2Ya/BONO_4_CODIGO.png',
  bonus5: 'https://cdn.magicpatterns.com/uploads/chmcepvxwhewHCZDGT4LRe/BONO_5_CODIGO.png',
  bonus6: 'https://cdn.magicpatterns.com/uploads/t9wzbcxrkpBybcwuCvGnMi/BONO_6_CODIGO.png',
  bonus7: 'https://cdn.magicpatterns.com/uploads/4qaFEMGuiV9zKpQb9o9syJ/BONO_7_CODIGO.png',
  bonus8: 'https://cdn.magicpatterns.com/uploads/uuFMGLJDJj7urkinXD7w6L/BONO_8_CODIGO.png'
};
const forYou = ['Sientes que repites los mismos patrones una y otra vez.', 'Quieres conocerte mejor antes de seguir buscando respuestas externas.', 'Buscas una guía práctica y fácil de aplicar.', 'Te interesa el crecimiento personal desde un enfoque estructurado.', 'Deseas tomar decisiones con mayor claridad.', 'Quieres fortalecer tu confianza y equilibrio personal.', 'Buscas herramientas que puedas utilizar a tu propio ritmo.'];
const learn = ['Identificar tu Código Energético Dominante.', 'Comprender las fortalezas y desafíos de cada Código.', 'Detectar patrones que pueden influir en tus decisiones.', 'Interpretar cómo esos patrones aparecen en el trabajo, las relaciones y tu desarrollo personal.', 'Crear un plan de crecimiento adaptado a tu perfil.', 'Incorporar hábitos sencillos para fortalecer tus puntos fuertes.', 'Dar seguimiento a tu evolución mediante ejercicios prácticos y un diario personal.'];
const results = ['Comprender mejor cómo reaccionas ante distintas situaciones.', 'Reconocer tus fortalezas naturales.', 'Identificar áreas que deseas desarrollar.', 'Tomar decisiones con mayor claridad.', 'Crear hábitos más alineados con tus objetivos.', 'Contar con una guía práctica que podrás consultar siempre que lo necesites.'];
const bonuses = [['Bono 1', 'El Diccionario de las Señales™', 'Aprende a interpretar símbolos, sincronías y señales desde un enfoque práctico.', images.bonus1], ['Bono 2', 'Los 99 Bloqueos Ocultos', 'Una guía para identificar creencias, hábitos y patrones que pueden estar limitando tu crecimiento.', images.bonus2], ['Bono 3', 'Reinicio Energético de 7 Días', 'Plan práctico para crear una rutina de bienestar durante una semana.', images.bonus3], ['Bono 4', 'Agenda Energética de 30 Días', 'Un planificador para registrar hábitos, objetivos y avances.', images.bonus4], ['Bono 5', 'Guía de Interpretación de Velas', 'Introducción a los significados tradicionales asociados a diferentes tipos de velas y colores.', images.bonus5], ['Bono 6', 'Manual de Protección Energética Cotidiana', 'Hábitos diarios para crear espacios de calma, organización y bienestar.', images.bonus6], ['Bono 7', 'Biblioteca de Afirmaciones según tu Código', 'Más de 300 afirmaciones organizadas según cada Código Energético.', images.bonus7], ['Bono 8', 'Mapa Energético del Hogar', 'Guía práctica para organizar los espacios de tu hogar con intención y equilibrio.', images.bonus8]];
const testimonials = [['Laura M.', '34 años · México', 'Me ayudó a entender patrones que llevaba años repitiendo. Los ejercicios son muy fáciles de aplicar y el contenido está muy bien organizado.', 'https://randomuser.me/api/portraits/women/44.jpg'], ['Carolina R.', '41 años · Colombia', 'Pensé que sería otro ebook más, pero encontré una metodología muy clara. El diagnóstico fue una excelente forma de comenzar.', 'https://randomuser.me/api/portraits/women/65.jpg'], ['Daniela P.', '29 años · Argentina', 'Los bonos complementan perfectamente el contenido principal. La Agenda de 30 Días fue mi favorita.', 'https://randomuser.me/api/portraits/women/32.jpg'], ['Paula G.', '38 años · Chile', 'Me gustó poder avanzar a mi ritmo y volver a los ejercicios cuando lo necesitaba. Todo se siente muy claro.', 'https://randomuser.me/api/portraits/women/79.jpg'], ['Marta L.', '46 años · España', 'Buscaba una guía concreta para ordenar mis ideas. Encontré prácticas sencillas que ya incorporé en mi semana.', 'https://randomuser.me/api/portraits/women/51.jpg']];
export function EnergyCodeLanding() {
  useScreenInit();
  return <main className="min-h-screen overflow-hidden bg-[#fbf6ea] text-[#162137]" data-id="element-26">
      <PurchaseNotification data-id="element-27" />
      <header className="border-b border-[#d3bd8b] bg-[#111a2a] px-5 py-3 text-center text-xs font-bold uppercase tracking-[0.12em] text-[#f2c86f]" data-id="element-28">
        Acceso digital inmediato · Pago único
      </header>

      <section className="bg-[#111a2a] px-5 pb-14 pt-12 text-center text-[#fff8e9] sm:pb-20 sm:pt-16" data-id="element-29">
        <div className="mx-auto max-w-3xl" data-id="element-30">
          <p className="mb-4 text-xs font-extrabold uppercase tracking-[0.2em] text-[#e2af4e]" data-id="element-31">
            Método C.E.P.™
          </p>
          <h1 className="mx-auto max-w-[22rem] font-serif text-[clamp(2.05rem,8.7vw,3.6rem)] font-bold leading-[1.12] tracking-[-0.025em] sm:max-w-4xl sm:text-6xl" data-id="element-32">
            Descubre tu{' '}
            <span className="text-[#e2af4e]" data-id="element-33">Código Energético Personal™</span>{' '}
            y comprende los patrones que influyen en tus decisiones, relaciones
            y bienestar
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-base leading-7 text-[#c9b99c] sm:text-lg" data-id="element-34">
            El sistema práctico que te ayuda a identificar tu perfil energético,
            reconocer tus fortalezas y desarrollar hábitos más conscientes en
            menos de una semana.
          </p>
          <a href={CHECKOUT_URL} className="mx-auto my-8 block w-full max-w-[335px] focus-visible:outline focus-visible:outline-4 focus-visible:outline-[#f2c86f]" data-id="element-35">
            <motion.img whileHover={{
            scale: 1.025
          }} src={images.product} alt="Portada del ebook Código Energético Personal" className="w-full shadow-2xl" data-id="element-36" />
          </a>
          <div className="mx-auto mt-8 grid max-w-2xl gap-3 text-left sm:grid-cols-2" data-id="element-37">
            {['Diagnóstico completo de tu Código Energético.', 'Método C.E.P.™ explicado paso a paso.', '9 perfiles energéticos desarrollados.', 'Ejercicios prácticos para aplicar desde el primer día.', 'Plan de transformación de 21 días.', '8 bonos exclusivos incluidos.'].map(item => <CheckLine key={item} dark data-id="element-38">
                {item}
              </CheckLine>)}
          </div>
          <p className="mt-7 text-sm font-bold text-[#e2af4e]" data-id="element-39">
            Más de 300 páginas de contenido digital.
          </p>
          <div className="mt-8" data-id="element-40">
            <CtaButton data-id="element-41">QUIERO ACCEDER AHORA</CtaButton>
            <a href={CHECKOUT_URL} aria-label="Acceder ahora al Código Energético Personal" className="fixed bottom-5 right-4 z-40 inline-flex items-center justify-center rounded-full bg-[#d49a35] px-5 py-3 text-xs font-extrabold tracking-[0.08em] text-[#111a2a] shadow-[0_4px_0_#8f5a16] transition hover:-translate-y-0.5 hover:bg-[#e4ad4b] focus-visible:outline focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-[#f2c86f] sm:bottom-6 sm:right-6 sm:px-6" data-id="element-42">
              ACCEDER AHORA
            </a>
            <SecureNote data-id="element-43" />
          </div>
        </div>
      </section>

      <ContentSection eyebrow="¿Este ebook es para ti?" title="Una guía práctica para comprenderte mejor" data-id="element-44">
        <p className="mx-auto max-w-2xl text-center text-lg leading-8 text-[#536073]" data-id="element-45">
          Este método fue diseñado para personas que desean comprenderse mejor y
          desarrollar hábitos más conscientes.
        </p>
        <div className="mx-auto mt-9 max-w-3xl rounded-sm border border-[#e4d5b5] bg-white p-6 shadow-sm sm:p-8" data-id="element-46">
          <p className="mb-5 font-serif text-xl font-bold" data-id="element-47">Es para ti si…</p>
          <div className="grid gap-3 sm:grid-cols-2" data-id="element-48">
            {forYou.map(item => <CheckLine key={item} data-id="element-49">{item}</CheckLine>)}
          </div>
        </div>
        <CenteredCta data-id="element-50">QUIERO CONOCER MI CÓDIGO</CenteredCta>
      </ContentSection>

      <ContentSection navy eyebrow="Lo que encontrarás dentro" title="Un recorrido completo para desarrollar mayor autoconocimiento" data-id="element-51">
        <p className="mx-auto max-w-2xl text-center text-lg leading-8 text-[#d8cdb6]" data-id="element-52">
          Dentro del Método C.E.P.™ encontrarás un recorrido completo para
          desarrollar mayor autoconocimiento.
        </p>
        <div className="mx-auto mt-9 grid max-w-4xl gap-x-12 gap-y-4 sm:grid-cols-2" data-id="element-53">
          {learn.map(item => <CheckLine key={item} dark data-id="element-54">
              {item}
            </CheckLine>)}
        </div>
        <CenteredCta data-id="element-55">QUIERO EMPEZAR HOY</CenteredCta>
      </ContentSection>

      <ContentSection eyebrow="Tu siguiente paso" title="¿Qué vas a lograr?" data-id="element-56">
        <div className="mx-auto grid max-w-4xl gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-57">
          {results.map(item => <article key={item} className="border border-[#e4d5b5] bg-white p-5 shadow-sm" data-id="element-58">
              <BadgeCheckIcon aria-hidden="true" className="mb-3 h-7 w-7 text-[#9b6b21]" data-id="element-59" />
              <p className="leading-6" data-id="element-60">{item}</p>
            </article>)}
        </div>
      </ContentSection>

      <section className="border-y border-[#b77b25] bg-[#eee0c5] px-5 py-14 sm:py-20" data-id="element-61">
        <div className="mx-auto max-w-5xl text-center" data-id="element-62">
          <p className="text-xs font-extrabold uppercase tracking-[0.2em] text-[#80581e]" data-id="element-63">
            Incluidos gratis por hoy
          </p>
          <h2 className="mt-3 font-serif text-4xl font-bold text-[#162137] sm:text-5xl" data-id="element-64">
            8 bonos exclusivos
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-lg leading-8 text-[#4b5361]" data-id="element-65">
            Además del ebook principal, hoy recibirás sin costo adicional estas
            herramientas para acompañar tu práctica.
          </p>
          <div className="mx-auto mt-10 max-w-3xl space-y-8" data-id="element-66">
            {bonuses.map(([number, title, description, image]) => <article key={number} className="overflow-hidden border border-[#c5aa75] bg-[#fffaf0] text-left shadow-md" data-id="element-67">
                {image ? <a href={CHECKOUT_URL} className="block bg-[#172236] p-4 focus-visible:outline focus-visible:outline-4 focus-visible:outline-[#d49a35]" data-id="element-68">
                    <img src={image} alt={`${number}: ${title}`} className="mx-auto max-h-[540px] w-auto max-w-full" data-id="element-69" />
                  </a> : <div className="flex min-h-28 items-center justify-center bg-[#172236]" data-id="element-70">
                    <BookOpenCheckIcon aria-hidden="true" className="h-12 w-12 text-[#e2af4e]" data-id="element-71" />
                  </div>}
                <div className="p-6" data-id="element-72">
                  <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-[#9b6b21]" data-id="element-73">
                    {number} · gratis hoy
                  </p>
                  <h3 className="mt-2 font-serif text-2xl font-bold" data-id="element-74">
                    {title}
                  </h3>
                  <p className="mt-2 leading-6 text-[#596274]" data-id="element-75">{description}</p>
                </div>
              </article>)}
          </div>
        </div>
      </section>

      <section id="compra" className="bg-[#111a2a] px-5 py-16 text-center text-[#fff8e9] sm:py-20" data-id="element-76">
        <div className="mx-auto max-w-2xl border border-[#b77b25] bg-[#172236] p-7 shadow-2xl sm:p-10" data-id="element-77">
          <p className="text-xs font-extrabold uppercase tracking-[0.2em] text-[#e2af4e]" data-id="element-78">
            Oferta de acceso digital
          </p>
          <h2 className="mt-3 font-serif text-4xl font-bold sm:text-5xl" data-id="element-79">
            Todo este paquete por
          </h2>
          <p className="mt-7 text-lg text-[#d8cdb6]" data-id="element-80">
            Valor estimado del paquete:{' '}
            <span className="line-through" data-id="element-81">USD $127</span>
          </p>
          <p className="mt-2 text-sm font-bold uppercase tracking-widest text-[#e2af4e]" data-id="element-82">
            Hoy puedes acceder por solo
          </p>
          <p className="font-serif text-6xl font-bold text-[#f2c86f] sm:text-7xl" data-id="element-83">
            USD $12.99
          </p>
          <p className="mt-3 text-sm text-[#d8cdb6]" data-id="element-84">
            Pago único. Acceso inmediato. Sin suscripciones.
          </p>
          <Countdown data-id="element-85" />
          <div className="mt-8" data-id="element-86">
            <CtaButton data-id="element-87">QUIERO MI ACCESO AHORA</CtaButton>
            <SecureNote data-id="element-88" />
          </div>
        </div>
      </section>

      <ContentSection eyebrow="Experiencias compartidas" title="Lo que dicen quienes ya comenzaron" data-id="element-89">
        <div className="mx-auto grid max-w-6xl gap-5 md:grid-cols-2 lg:grid-cols-3" data-id="element-90">
          {testimonials.map(([name, info, quote, imageUrl]) => <article key={name} className="border border-[#e4d5b5] bg-white p-6 shadow-sm" data-id="element-91">
              <div className="flex gap-1 text-[#bc8126]" data-id="element-92">
                {Array.from({
              length: 5
            }).map((_, i) => <StarIcon key={i} aria-hidden="true" className="h-4 w-4 fill-current" data-id="element-93" />)}
              </div>
              <p className="mt-4 leading-7 text-[#354156]" data-id="element-94">“{quote}”</p>
              <div className="mt-5 flex items-center gap-3" data-id="element-95">
                <img src={imageUrl} alt={`Foto de perfil de ${name}`} className="h-14 w-14 shrink-0 rounded-full border-2 border-[#d2b171] bg-[#ead8b3] object-cover" data-id="element-96" />
                <div data-id="element-97">
                  <p className="font-bold" data-id="element-98">{name}</p>
                  <p className="text-sm text-[#737b89]" data-id="element-99">{info}</p>
                </div>
              </div>
            </article>)}
        </div>
        <CenteredCta data-id="element-100">QUIERO ACCEDER AL MÉTODO</CenteredCta>
      </ContentSection>

      <section className="bg-[#eee0c5] px-5 py-14 sm:py-20" data-id="element-101">
        <div className="mx-auto max-w-3xl text-center" data-id="element-102">
          <ShieldCheckIcon aria-hidden="true" className="mx-auto h-12 w-12 text-[#80581e]" data-id="element-103" />
          <p className="mt-4 text-xs font-extrabold uppercase tracking-[0.2em] text-[#80581e]" data-id="element-104">
            Garantía de 7 días
          </p>
          <h2 className="mt-3 font-serif text-4xl font-bold" data-id="element-105">
            Compra con total tranquilidad.
          </h2>
          <p className="mt-5 text-lg leading-8 text-[#4b5361]" data-id="element-106">
            Si durante los primeros 7 días consideras que el contenido no
            cumplió con tus expectativas, podrás solicitar la devolución de tu
            dinero.
          </p>
          <p className="mt-4 font-bold text-[#26354a]" data-id="element-107">
            Queremos que pruebes el Método C.E.P.™ con confianza. No asumes
            ningún riesgo.
          </p>
        </div>
      </section>

      <ContentSection eyebrow="Preguntas frecuentes" title="Todo lo que necesitas saber" data-id="element-108">
        <div className="mx-auto max-w-3xl divide-y divide-[#e4d5b5] border-y border-[#e4d5b5] bg-white" data-id="element-109">
          <Faq question="¿Cómo recibiré el producto?" data-id="element-110">
            Recibirás acceso inmediato después de completar tu compra.
          </Faq>
          <Faq question="¿Necesito conocimientos previos?" data-id="element-111">
            No. Todo el contenido está explicado paso a paso.
          </Faq>
          <Faq question="¿Puedo verlo desde mi celular?" data-id="element-112">
            Sí. Podrás acceder desde computadora, tablet o teléfono móvil.
          </Faq>
          <Faq question="¿El acceso tiene vencimiento?" data-id="element-113">
            No. El acceso es tuyo para siempre.
          </Faq>
          <Faq question="¿Cuánto tiempo necesito para completar el método?" data-id="element-114">
            Puedes avanzar a tu propio ritmo. Muchas personas completan el
            contenido principal durante la primera semana y luego continúan
            utilizando los ejercicios y herramientas como guía de consulta.
          </Faq>
          <Faq question="¿Los bonos están incluidos?" data-id="element-115">Sí.</Faq>
        </div>
        <CenteredCta data-id="element-116">QUIERO MI ACCESO AHORA</CenteredCta>
      </ContentSection>

      <footer className="bg-[#111a2a] px-5 py-10 text-center text-sm text-[#c9b99c]" data-id="element-117">
        <SparklesIcon aria-hidden="true" className="mx-auto mb-3 h-5 w-5 text-[#e2af4e]" data-id="element-118" />
        <p className="font-serif text-xl text-[#fff8e9]" data-id="element-119">
          Código Energético Personal™
        </p>
        <p className="mt-2" data-id="element-120">
          Una guía digital para tu proceso de autoconocimiento.
        </p>
      </footer>
    </main>;
}
function ContentSection({
  eyebrow,
  title,
  children,
  navy = false
}: {
  eyebrow: string;
  title: string;
  children: React.ReactNode;
  navy?: boolean;
}) {
  return <section className={`${navy ? 'bg-[#172236] text-[#fff8e9]' : 'bg-[#fbf6ea]'} px-5 py-14 sm:py-20`} data-id="element-121">
      <div className="mx-auto max-w-6xl" data-id="element-122">
        <p className={`text-center text-xs font-extrabold uppercase tracking-[0.2em] ${navy ? 'text-[#e2af4e]' : 'text-[#9b6b21]'}`} data-id="element-123">
          {eyebrow}
        </p>
        <h2 className="mx-auto mt-3 max-w-3xl text-center font-serif text-4xl font-bold leading-tight sm:text-5xl" data-id="element-124">
          {title}
        </h2>
        <div className="mt-8" data-id="element-125">{children}</div>
      </div>
    </section>;
}
function CheckLine({
  children,
  dark = false
}: {
  children: React.ReactNode;
  dark?: boolean;
}) {
  return <div className={`flex gap-3 text-sm leading-6 ${dark ? 'text-[#eee4d1]' : 'text-[#3f4a5c]'}`} data-id="element-126">
      <CheckIcon aria-hidden="true" className="mt-1 h-4 w-4 shrink-0 text-[#c58c2c]" strokeWidth={3} data-id="element-127" />
      <span data-id="element-128">{children}</span>
    </div>;
}
function CenteredCta({
  children
}: {
  children: React.ReactNode;
}) {
  return <div className="mt-10 text-center" data-id="element-129">
      <CtaButton data-id="element-130">{children}</CtaButton>
    </div>;
}
function Faq({
  question,
  children
}: {
  question: string;
  children: React.ReactNode;
}) {
  return <details className="group p-6" data-id="element-131">
      <summary className="cursor-pointer list-none pr-8 font-serif text-xl font-bold marker:hidden" data-id="element-132">
        {question}
        <span aria-hidden="true" className="float-right text-[#9b6b21] group-open:hidden" data-id="element-133">
          +
        </span>
        <span aria-hidden="true" className="float-right text-[#9b6b21] group-open:inline hidden" data-id="element-134">
          −
        </span>
      </summary>
      <p className="pt-4 leading-7 text-[#596274]" data-id="element-135">{children}</p>
    </details>;
}